import React, { useState, useEffect, Component, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { 
  Gavel, Calendar, FileText, Download, Plus, X, Send, Clock,
  CheckCircle2, Building2, DollarSign, Lock, AlertCircle, Upload, Sparkles, Loader2,
  MessageSquare, ChevronDown, ChevronRight, Edit2, Mail
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { apiService } from '@/lib/api';
import axios from 'axios';
import { formatDate } from '@/lib/dateUtils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Error Boundary to catch any rendering errors
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('[ContractorBidPortal] Error caught by boundary:', error, errorInfo);
    // Report the crash so it surfaces in the owner's error log + email alert.
    try {
      const params = new URLSearchParams(window.location.search);
      axios.post(`${API}/telemetry/client-error`, {
        source: 'contractor_portal',
        message: error?.message || String(error),
        stack: (errorInfo?.componentStack || error?.stack || '').slice(0, 4000),
        url: window.location.href,
        contractor_email: params.get('email') || null,
        user_agent: navigator.userAgent,
        severity: 'critical',
      }).catch(() => {});
    } catch (e) { /* never let reporting break the boundary */ }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
          <Card className="max-w-md w-full">
            <CardContent className="pt-6">
              <div className="text-center">
                <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
                <h2 className="text-xl font-bold mb-2">Something Went Wrong</h2>
                <p className="text-slate-600 mb-4">
                  We encountered an error loading this page. Please try refreshing.
                </p>
                <p className="text-xs text-red-500 mb-4 break-all">
                  {this.state.error?.message || 'Unknown error'}
                </p>
                <Button onClick={() => window.location.reload()}>
                  Refresh Page
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}

/* v2.5.1 helpers: contractor duration input is days/weeks/months but we
   store integer days on the backend for consistent sorting + reporting. */
const daysToDurationDisplay = (days) => {
  const n = parseInt(days, 10);
  if (!n || n <= 0) return { value: '', unit: 'days' };
  if (n % 30 === 0) return { value: String(n / 30), unit: 'months' };
  if (n % 7 === 0) return { value: String(n / 7), unit: 'weeks' };
  return { value: String(n), unit: 'days' };
};

const durationToDays = (value, unit) => {
  const n = parseFloat(value);
  if (!Number.isFinite(n) || n <= 0) return null;
  const factor = unit === 'months' ? 30 : unit === 'weeks' ? 7 : 1;
  return Math.round(n * factor);
};



function ContractorBidPortalInner() {
  const [searchParams] = useSearchParams();
  const contractorEmail = searchParams.get('email');
  const quickUploadToken = searchParams.get('quick_upload');
  const revisionToken = searchParams.get('revision');
  
  const [activeTab, setActiveTab] = useState('bids');
  const [bidRequests, setBidRequests] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedBid, setSelectedBid] = useState(null);
  const [error, setError] = useState(null); // Add error state
  
  // Bid response form
  const [lineItems, setLineItems] = useState([]);
  const [estimatedDays, setEstimatedDays] = useState('');
  // v2.5.1: split duration into value + unit so contractor can express in
  // days / weeks / months. Backend still stores integer days — we convert
  // at the I/O boundaries (load + submit). Keeps the data shape stable.
  const [durationValue, setDurationValue] = useState('');
  const [durationUnit, setDurationUnit] = useState('days');
  // v2.5.5.5: per-stage breakdown. When the bid's trade has multiple stages
  // (Plumbing → Ground/Rough/Trim, etc.) we render an array of {stage, days}
  // inputs instead of the single duration field.
  // Shape: [{stage_name, days, is_custom}]
  const [stageBreakdown, setStageBreakdown] = useState([]);
  const [tradeStageMap, setTradeStageMap] = useState({});
  const [stageDefaults, setStageDefaults] = useState({});
  const [stageRequiresCrew, setStageRequiresCrew] = useState(new Set());
  // v2.5.5.7: per-building support for the bid portal — when a project has
  // multiple buildings the contractor can either give one uniform set of
  // numbers (sameForAllBuildings = true) or fill a per-building grid.
  // perBuildingStages is shape { [bIdx]: { [stage_name]: days } } and is only
  // used when sameForAllBuildings === false.
  const [sameForAllBuildings, setSameForAllBuildings] = useState(true);
  const [perBuildingStages, setPerBuildingStages] = useState({});
  const [newStageName, setNewStageName] = useState('');
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // AI Extraction state
  const [isExtracting, setIsExtracting] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);

  // Schedule state
  const [scheduleItems, setScheduleItems] = useState([]);
  const [messages, setMessages] = useState({});
  const [showApprovalDialog, setShowApprovalDialog] = useState(false);
  const [showMessageDialog, setShowMessageDialog] = useState(false);
  const [selectedScheduleItem, setSelectedScheduleItem] = useState(null);
  const [changeReason, setChangeReason] = useState('');
  const [messageText, setMessageText] = useState('');
  const [expandedSchedule, setExpandedSchedule] = useState({});
  
  // Bid messaging state
  const [bidMessages, setBidMessages] = useState({});
  const [showBidMessageDialog, setShowBidMessageDialog] = useState(false);
  const [bidMessageText, setBidMessageText] = useState('');
  const [isSendingBidMessage, setIsSendingBidMessage] = useState(false);
  
  // Full messaging state (conversations)
  const [showMessagesView, setShowMessagesView] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [selectedClientConversation, setSelectedClientConversation] = useState(null);
  const messagesEndRef = useRef(null);

  // Quick Upload state
  const [quickUploadMode, setQuickUploadMode] = useState(false);

  // v2.5.2: account info + Set Password modal
  const [accountInfo, setAccountInfo] = useState(null);
  const [showSetPasswordModal, setShowSetPasswordModal] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSettingPassword, setIsSettingPassword] = useState(false);
  const [quickInviteData, setQuickInviteData] = useState(null);
  const [quickUploadFile, setQuickUploadFile] = useState(null);
  const [quickUploadNotes, setQuickUploadNotes] = useState('');
  const [isUploadingQuickBid, setIsUploadingQuickBid] = useState(false);
  const [quickUploadSuccess, setQuickUploadSuccess] = useState(false);
  const [quickInviteError, setQuickInviteError] = useState(null);

  // Revision mode state
  const [revisionMode, setRevisionMode] = useState(false);
  const [revisionData, setRevisionData] = useState(null);
  const [revisionLineItems, setRevisionLineItems] = useState([]);
  const [revisionTotal, setRevisionTotal] = useState(0);
  const [revisionNotes, setRevisionNotes] = useState('');
  const [isSubmittingRevision, setIsSubmittingRevision] = useState(false);
  const [revisionSuccess, setRevisionSuccess] = useState(false);
  const [revisionError, setRevisionError] = useState(null);

  useEffect(() => {
    if (revisionToken) {
      // Revision mode - load revision request data
      loadRevisionData();
    } else if (quickUploadToken) {
      // Quick upload mode - load invite data
      loadQuickInvite();
    } else if (contractorEmail) {
      console.log('[ContractorBidPortal] Email from URL:', contractorEmail);
      loadBidRequests();
      loadSchedule();
    } else {
      console.log('[ContractorBidPortal] No email parameter in URL');
      setIsLoading(false);
    }
  }, [contractorEmail, quickUploadToken, revisionToken]);

  // v2.5.5.5: load the trade→stage map once on mount so handleSelectBid can
  // figure out whether to render the per-stage inputs.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await apiService.getTradeStages();
        if (cancelled) return;
        setTradeStageMap(r.data?.trade_stages || {});
        setStageDefaults(r.data?.stage_defaults || {});
        setStageRequiresCrew(new Set((r.data?.stage_requires_crew_size || []).map(s => s.toLowerCase())));
      } catch (e) {
        // Non-fatal — falls back to single duration input for every trade.
        console.warn('[ContractorBidPortal] Failed to load trade stages:', e);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const loadRevisionData = async () => {
    try {
      setIsLoading(true);
      setRevisionError(null);
      const res = await apiService.getBidRevision(revisionToken);
      setRevisionData(res.data);
      setRevisionMode(true);
      
      // Pre-populate with existing bid data
      if (res.data.bid_response) {
        setRevisionLineItems(res.data.bid_response.line_items || []);
        setRevisionTotal(res.data.bid_response.total_amount || 0);
        setRevisionNotes(res.data.bid_response.notes || '');
      }
    } catch (error) {
      console.error('[ContractorBidPortal] Failed to load revision data:', error);
      const errorMsg = error.response?.data?.detail || 'Invalid or expired revision link';
      setRevisionError(errorMsg);
      toast.error(errorMsg);
      // Still set revisionMode to true so we can show the error in context
      setRevisionMode(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmitRevision = async () => {
    if (revisionLineItems.length === 0 && !revisionTotal) {
      toast.error('Please add line items or a total amount');
      return;
    }

    try {
      setIsSubmittingRevision(true);
      await apiService.submitBidRevision(revisionToken, {
        line_items: revisionLineItems,
        total_amount: parseFloat(revisionTotal) || 0,
        notes: revisionNotes
      });
      
      toast.success('Bid revised successfully!');
      setRevisionSuccess(true);
    } catch (error) {
      console.error('[ContractorBidPortal] Revision submission failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit revision');
    } finally {
      setIsSubmittingRevision(false);
    }
  };

  const addRevisionLineItem = () => {
    setRevisionLineItems([...revisionLineItems, {
      id: Date.now().toString(),
      description: '',
      quantity: 1,
      unit: '',
      unit_price: 0,
      total: 0,
      unit_type: 'flat'
    }]);
  };

  const updateRevisionLineItem = (index, field, value) => {
    const updated = [...revisionLineItems];
    updated[index][field] = value;
    
    // Recalculate total
    if (field === 'quantity' || field === 'unit_price') {
      updated[index].total = (parseFloat(updated[index].quantity) || 0) * (parseFloat(updated[index].unit_price) || 0);
    }
    
    setRevisionLineItems(updated);
    
    // Update grand total
    const grandTotal = updated.reduce((sum, item) => sum + (parseFloat(item.total) || 0), 0);
    setRevisionTotal(grandTotal);
  };

  const removeRevisionLineItem = (index) => {
    const updated = revisionLineItems.filter((_, i) => i !== index);
    setRevisionLineItems(updated);
    const grandTotal = updated.reduce((sum, item) => sum + (parseFloat(item.total) || 0), 0);
    setRevisionTotal(grandTotal);
  };

  const loadQuickInvite = async () => {
    try {
      setIsLoading(true);
      setQuickInviteError(null);
      const res = await apiService.getQuickBidInvite(quickUploadToken);
      setQuickInviteData(res.data);
      setQuickUploadMode(true);
    } catch (error) {
      console.error('[ContractorBidPortal] Failed to load quick invite:', error);
      const errorMsg = error.response?.data?.detail || 'Invalid or expired invite link';
      setQuickInviteError(errorMsg);
      toast.error(errorMsg);
      // Still set quickUploadMode to true so we can show the error in context
      setQuickUploadMode(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleQuickBidUpload = async () => {
    if (!quickUploadFile) {
      toast.error('Please select a file to upload');
      return;
    }

    try {
      setIsUploadingQuickBid(true);
      const res = await apiService.submitQuickBid(quickUploadToken, quickUploadFile, quickUploadNotes);
      
      toast.success(res.data.message || 'Bid uploaded successfully!');
      setQuickUploadSuccess(true);
    } catch (error) {
      console.error('[ContractorBidPortal] Quick bid upload failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload bid');
    } finally {
      setIsUploadingQuickBid(false);
    }
  };

  const loadSchedule = async () => {
    try {
      const res = await apiService.getContractorSchedule(contractorEmail);
      setScheduleItems(res.data?.schedule_items || []);
    } catch (error) {
      console.error('[ContractorBidPortal] Failed to load schedule:', error);
    }
  };

  const loadMessages = async (projectId) => {
    try {
      const res = await apiService.getContractorMessages(contractorEmail, projectId);
      setMessages(prev => ({ ...prev, [projectId]: res.data || [] }));
    } catch (error) {
      console.error('[ContractorBidPortal] Failed to load messages:', error);
    }
  };

  // Bid-related messaging functions
  const loadBidMessages = async (bidRequestId) => {
    if (!contractorEmail || !bidRequestId) return;
    try {
      const res = await apiService.getContractorMessages(contractorEmail, null, bidRequestId);
      setBidMessages(prev => ({ ...prev, [bidRequestId]: res.data || [] }));
    } catch (error) {
      console.error('[ContractorBidPortal] Failed to load bid messages:', error);
    }
  };

  const handleSendBidMessage = async () => {
    if (!bidMessageText.trim() || !selectedBid) return;
    
    setIsSendingBidMessage(true);
    try {
      await apiService.sendContractorMessage(
        contractorEmail,
        bidMessageText.trim(),
        selectedBid.project_id,
        selectedBid.id, // bid_request_id
        null, // phase_id
        `Question about: ${selectedBid.title}`
      );
      toast.success('Message sent! The client will be notified.');
      setShowBidMessageDialog(false);
      setBidMessageText('');
      loadBidMessages(selectedBid.id);
    } catch (error) {
      console.error('Failed to send bid message:', error);
      toast.error('Failed to send message');
    } finally {
      setIsSendingBidMessage(false);
    }
  };

  const handleScheduleResponse = async (status) => {
    if (!selectedScheduleItem) return;
    
    try {
      // Support both phase-based (legacy) and item-based (Option 3) approvals
      const phaseId = selectedScheduleItem.phase_id || null;
      const approvalId = selectedScheduleItem.approval_id || selectedScheduleItem.id || null;
      
      await apiService.respondToSchedule(contractorEmail, phaseId, approvalId, {
        status,
        change_request_reason: status === 'change_requested' ? changeReason : null
      });
      toast.success(status === 'approved' ? 'Schedule approved!' : 'Change request submitted');
      setShowApprovalDialog(false);
      setChangeReason('');
      loadSchedule();
    } catch (error) {
      console.error('Schedule response error:', error);
      toast.error('Failed to respond to schedule');
    }
  };

  // Load all conversations for this contractor
  const loadConversations = async () => {
    if (!contractorEmail) return;
    try {
      const res = await apiService.contractorGetConversations(contractorEmail);
      setConversations(res.data?.conversations || []);
    } catch (error) {
      console.error('Failed to load conversations:', error);
    }
  };

  const handleSelectClientConversation = async (conv) => {
    setSelectedClientConversation(conv);
    // Mark as read
    try {
      await apiService.contractorMarkRead(contractorEmail, conv.client_id);
      loadConversations();
    } catch (error) {
      console.error('Failed to mark as read:', error);
    }
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  const handleSendToClient = async () => {
    if (!bidMessageText.trim() || !selectedClientConversation) return;
    
    setIsSendingBidMessage(true);
    try {
      await apiService.sendContractorMessage(
        contractorEmail,
        bidMessageText.trim(),
        null, // project_id
        null, // bid_request_id
        null, // phase_id
        null  // subject
      );
      toast.success('Message sent!');
      setBidMessageText('');
      loadConversations();
      // Reload messages
      const conv = conversations.find(c => c.client_id === selectedClientConversation.client_id);
      if (conv) setSelectedClientConversation(conv);
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setIsSendingBidMessage(false);
    }
  };

  const handleSendMessage = async () => {
    if (!messageText.trim() || !selectedScheduleItem) return;
    
    try {
      await apiService.sendContractorMessage(
        contractorEmail, 
        messageText,
        selectedScheduleItem.project_id, 
        null,
        selectedScheduleItem.phase_id
      );
      toast.success('Message sent');
      setShowMessageDialog(false);
      setMessageText('');
      loadMessages(selectedScheduleItem.project_id);
    } catch (error) {
      toast.error('Failed to send message');
    }
  };

  const loadBidRequests = async () => {
    try {
      setIsLoading(true);
      setError(null);
      console.log('[ContractorBidPortal] Fetching bid requests for:', contractorEmail);
      const [bidsRes, acctRes] = await Promise.all([
        apiService.getContractorBidRequests(contractorEmail),
        // v2.5.2: fetch account info so we can offer Set Password
        axios.get(`${API}/contractor-portal/account?email=${encodeURIComponent(contractorEmail)}`).catch(() => null),
      ]);
      console.log('[ContractorBidPortal] Received', bidsRes.data?.length || 0, 'bid requests');
      setBidRequests(bidsRes.data || []);
      if (acctRes?.data) setAccountInfo(acctRes.data);
    } catch (err) {
      console.error('[ContractorBidPortal] Failed to load bid requests:', err);
      setError(err.response?.data?.detail || 'Failed to load bid requests. Please try again.');
      toast.error('Failed to load bid requests');
      setBidRequests([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSetPassword = async () => {
    if (!accountInfo?.portal_token) {
      toast.error('Account info not loaded');
      return;
    }
    if (!newPassword || newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    setIsSettingPassword(true);
    try {
      await axios.post(`${API}/portal/v2/auth/set-password`, {
        token: accountInfo.portal_token,
        password: newPassword,
      });
      toast.success('Password set! You can now log in via the contractor login page.');
      setAccountInfo(prev => ({ ...prev, has_password: true }));
      setShowSetPasswordModal(false);
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to set password');
    } finally {
      setIsSettingPassword(false);
    }
  };

  const handleSelectBid = (bid) => {
    setSelectedBid(bid);
    // v2.5.5.5/v2.5.5.7: figure out if THIS bid's trade has a multi-stage
    // breakdown and reset per-building state. The backend may also send back
    // an ``effective_stages`` list (canonical + the owner's learned custom
    // stages) — prefer that when present.
    const tradeKey = (bid.trade_type || '').trim().toLowerCase();
    const stages = (Array.isArray(bid.effective_stages) && bid.effective_stages.length > 0)
      ? bid.effective_stages
      : (tradeStageMap[tradeKey] || []);
    // Stages from owner's library beyond canonical are marked as suggested
    // (still custom underneath, but pre-populated so contractor sees them).
    const canonicalSet = new Set((tradeStageMap[tradeKey] || []).map(s => s.toLowerCase()));
    setSameForAllBuildings(true);
    setPerBuildingStages({});
    setNewStageName('');
    if (stages.length > 0) {
      // Hydrate from existing response if any, otherwise empty array (forces fill).
      const existing = bid.response?.stage_breakdown || [];
      // Build the "uniform" stage list — entries with building_index null
      // OR distinct stage names (legacy data has no building_index).
      const uniformEntries = existing.filter(s => s.building_index === null || s.building_index === undefined);
      const byName = Object.fromEntries(uniformEntries.map(s => [s.stage_name, s.days]));
      const customStages = uniformEntries
        .filter(s => s.is_custom && !stages.includes(s.stage_name))
        .map(s => s.stage_name);
      setStageBreakdown([
        ...stages.map(s => ({
          stage_name: s,
          days: byName[s] !== undefined ? byName[s] : '',
          crew_size: (uniformEntries.find(e => e.stage_name === s)?.crew_size) ?? '',
          // Stages outside the canonical TRADE_STAGE_MAP (i.e. owner's
          // learned library) are flagged is_custom so they round-trip
          // correctly when the contractor submits.
          is_custom: !canonicalSet.has(s.toLowerCase()),
        })),
        ...customStages.map(s => ({
          stage_name: s,
          days: byName[s] !== undefined ? byName[s] : '',
          crew_size: (uniformEntries.find(e => e.stage_name === s)?.crew_size) ?? '',
          is_custom: true,
        })),
      ]);
      // Restore per-building data if it exists
      const perBldg = existing.filter(s => s.building_index !== null && s.building_index !== undefined);
      if (perBldg.length > 0) {
        const pb = {};
        perBldg.forEach(s => {
          pb[s.building_index] = pb[s.building_index] || {};
          pb[s.building_index][s.stage_name] = s.days;
        });
        setPerBuildingStages(pb);
        setSameForAllBuildings(false);
      }
    } else {
      setStageBreakdown([]);
    }
    
    // Initialize line items from bid request template or existing response
    if (bid.response?.line_items?.length > 0) {
      setLineItems(bid.response.line_items.map(item => ({
        ...item,
        unit_price: item.unit_price || '',
        total: item.total || ''
      })));
      setEstimatedDays(bid.response.estimated_days || '');
      // v2.5.1: hydrate the duration input from stored integer days
      const _disp = daysToDurationDisplay(bid.response.estimated_days);
      setDurationValue(_disp.value);
      setDurationUnit(_disp.unit);
      setNotes(bid.response.notes || '');
    } else if (bid.line_items?.length > 0) {
      setLineItems(bid.line_items.map(item => ({
        description: item.description,
        unit: item.unit || '',
        quantity: item.quantity || '',
        unit_price: '',
        total: '',
        notes: ''
      })));
      setEstimatedDays('');
      setDurationValue('');
      setDurationUnit('days');
      setNotes('');
    } else {
      setLineItems([{ description: '', unit: '', quantity: '', unit_price: '', total: '', notes: '' }]);
      setEstimatedDays('');
      setDurationValue('');
      setDurationUnit('days');
      setNotes('');
    }
  };

  const addLineItem = () => {
    setLineItems([...lineItems, { description: '', unit: '', quantity: '', unit_price: '', total: '', notes: '' }]);
  };

  const removeLineItem = (index) => {
    setLineItems(lineItems.filter((_, i) => i !== index));
  };

  const updateLineItem = (index, field, value) => {
    const updated = [...lineItems];
    updated[index][field] = value;
    
    // Auto-calculate total if qty and unit_price are set
    if ((field === 'quantity' || field === 'unit_price') && updated[index].quantity && updated[index].unit_price) {
      updated[index].total = (parseFloat(updated[index].quantity) * parseFloat(updated[index].unit_price)).toFixed(2);
    }
    
    setLineItems(updated);
  };

  const calculateTotal = () => {
    return lineItems.reduce((sum, item) => sum + (parseFloat(item.total) || 0), 0);
  };

  const handleSubmitBid = async () => {
    if (!selectedBid) return;
    
    const validLineItems = lineItems.filter(item => item.description && item.total);
    if (validLineItems.length === 0) {
      toast.error('Please add at least one line item with a total');
      return;
    }
    
    try {
      setIsSubmitting(true);
      await apiService.submitBidResponse(contractorEmail, {
        bid_request_id: selectedBid.id,
        line_items: validLineItems.map(item => ({
          description: item.description,
          quantity: parseFloat(item.quantity) || null,
          unit: item.unit || null,
          unit_price: parseFloat(item.unit_price) || 0,
          total: parseFloat(item.total) || 0,
          notes: item.notes || null
        })),
        total_amount: calculateTotal(),
        // v2.5.1 / v2.5.5.5 / v2.5.5.7: derive integer days from EITHER the
        // per-stage breakdown (sum of stages — averaged across buildings when
        // per-building mode), OR the duration input, OR legacy estimatedDays.
        // Single source of truth: per-stage > new duration > legacy.
        estimated_days: (() => {
          if (stageBreakdown.length > 0) {
            if (sameForAllBuildings) {
              const sum = stageBreakdown.reduce((s, st) => s + (parseInt(st.days, 10) || 0), 0);
              if (sum > 0) return sum;
            } else {
              // Use max-per-stage across buildings as a conservative single
              // number for legacy consumers; per-building entries still flow
              // through stage_breakdown for accurate scheduling.
              const buildingCount = Math.max(1, parseInt(selectedBid?.project?.buildings || 1, 10));
              let total = 0;
              for (const st of stageBreakdown) {
                let maxDays = 0;
                for (let b = 0; b < buildingCount; b += 1) {
                  const v = parseInt((perBuildingStages[b] || {})[st.stage_name], 10) || 0;
                  if (v > maxDays) maxDays = v;
                }
                total += maxDays;
              }
              if (total > 0) return total;
            }
          }
          const fromNew = durationToDays(durationValue, durationUnit);
          if (fromNew !== null) return fromNew;
          return estimatedDays ? parseInt(estimatedDays) : null;
        })(),
        stage_breakdown: (() => {
          if (stageBreakdown.length === 0) return null;
          // helper to find the crew_size the contractor entered for a stage
          const crewFor = (stageName) => {
            const s = stageBreakdown.find(x => x.stage_name === stageName);
            const v = parseInt(s?.crew_size, 10);
            return Number.isFinite(v) && v > 0 ? v : null;
          };
          if (sameForAllBuildings) {
            return stageBreakdown
              .filter(s => s.stage_name && (parseInt(s.days, 10) || 0) > 0)
              .map(s => ({
                stage_name: s.stage_name,
                days: parseInt(s.days, 10) || 0,
                is_custom: !!s.is_custom,
                building_index: null,
                unit_index: null,
                crew_size: crewFor(s.stage_name),
              }));
          }
          // Per-building: emit one entry per (stage, building) with days > 0
          const out = [];
          const buildingCount = Math.max(1, parseInt(selectedBid?.project?.buildings || 1, 10));
          for (const st of stageBreakdown) {
            if (!st.stage_name) continue;
            for (let b = 0; b < buildingCount; b += 1) {
              const v = parseInt((perBuildingStages[b] || {})[st.stage_name], 10) || 0;
              if (v > 0) {
                out.push({
                  stage_name: st.stage_name,
                  days: v,
                  is_custom: !!st.is_custom,
                  building_index: b,
                  unit_index: null,
                  crew_size: crewFor(st.stage_name),
                });
              }
            }
          }
          return out.length > 0 ? out : null;
        })(),
        notes: notes || null
      });
      
      toast.success('Bid submitted successfully!');
      setSelectedBid(null);
      loadBidRequests();
    } catch (error) {
      console.error('Submit error:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit bid');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDownloadDocument = async (docId, filename) => {
    try {
      const response = await apiService.downloadDocument(docId);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Failed to download document');
    }
  };

  const handleDownloadScopeAttachment = async (bidRequestId, attachmentId, filename) => {
    try {
      const response = await apiService.downloadScopeAttachment(bidRequestId, attachmentId);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Failed to download attachment');
    }
  };

  // AI Bid Extraction - auto-populate form from uploaded document
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploadedFile(file);
    setIsExtracting(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const res = await apiService.extractBidData(formData);
      
      if (res.data?.success && res.data?.extracted_data) {
        const data = res.data.extracted_data;
        
        // Populate line items from extraction
        if (data.line_items && data.line_items.length > 0) {
          setLineItems(data.line_items.map(item => ({
            description: item.description || '',
            quantity: item.quantity?.toString() || '',
            unit: item.unit || '',
            unit_price: item.unit_price?.toString() || '',
            total: item.total?.toString() || '',
            notes: ''
          })));
        }
        
        // Set estimated days if available
        if (data.timeline?.estimated_days) {
          setEstimatedDays(data.timeline.estimated_days.toString());
          // v2.5.1: hydrate the duration input too
          const _disp = daysToDurationDisplay(data.timeline.estimated_days);
          setDurationValue(_disp.value);
          setDurationUnit(_disp.unit);
        }
        
        // Set notes from extraction
        const extractedNotes = [];
        if (data.bid_details?.scope_summary) {
          extractedNotes.push(data.bid_details.scope_summary);
        }
        if (data.notes && data.notes.length > 0) {
          extractedNotes.push(...data.notes);
        }
        if (data.pricing?.payment_terms) {
          extractedNotes.push(`Payment Terms: ${data.pricing.payment_terms}`);
        }
        if (extractedNotes.length > 0) {
          setNotes(extractedNotes.join('\n'));
        }
        
        toast.success(`Extracted ${data.line_items?.length || 0} line items from your bid document!`);
      } else {
        toast.error(res.data?.error || 'Could not extract data from document');
      }
    } catch (error) {
      console.error('Extraction error:', error);
      toast.error('Failed to extract bid data. Please enter details manually.');
    } finally {
      setIsExtracting(false);
    }
  };

  const getStatusBadge = (status, hasResponded) => {
    if (hasResponded) {
      return <Badge className="bg-green-100 text-green-700">Submitted</Badge>;
    }
    if (status === 'closed') {
      return <Badge className="bg-gray-100 text-gray-700">Closed</Badge>;
    }
    return <Badge className="bg-yellow-100 text-yellow-700">Pending Response</Badge>;
  };

  const getDaysRemaining = (dueDate) => {
    const due = new Date(dueDate);
    const today = new Date();
    const diff = Math.ceil((due - today) / (1000 * 60 * 60 * 24));
    return diff;
  };

  // Quick Upload Mode UI
  if (quickUploadMode) {
    if (isLoading) {
      return (
        <div className="min-h-screen bg-slate-100 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      );
    }

    // Show error state for quick invite failures
    if (quickInviteError) {
      return (
        <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
          <Card className="max-w-md w-full">
            <CardContent className="pt-8 pb-6 text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertCircle className="w-8 h-8 text-red-600" />
              </div>
              <h2 className="text-2xl font-bold text-slate-800 mb-2">Unable to Load Invite</h2>
              <p className="text-slate-600 mb-4">{quickInviteError}</p>
              <p className="text-sm text-slate-500 mb-6">
                This invite link may have expired or already been used. Please contact the project owner for a new invite link.
              </p>
              <Button 
                onClick={() => window.location.reload()} 
                variant="outline"
                data-testid="retry-quick-invite-btn"
              >
                Try Again
              </Button>
            </CardContent>
          </Card>
        </div>
      );
    }

    if (quickUploadSuccess) {
      return (
        <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
          <Card className="max-w-lg w-full">
            <CardContent className="pt-8 pb-6 text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-slate-800 mb-2">Bid Uploaded Successfully!</h2>
              <p className="text-slate-600 mb-6">
                Thank you for submitting your bid. {quickInviteData?.owner?.business_name || quickInviteData?.owner?.name || 'The project owner'} will review your submission and get back to you.
              </p>
              <div className="p-4 bg-slate-50 rounded-lg text-left">
                <p className="text-sm text-slate-500 mb-1">Project</p>
                <p className="font-medium">{quickInviteData?.project?.name}</p>
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }

    return (
      <div className="min-h-screen bg-slate-100">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white py-8">
          <div className="max-w-2xl mx-auto px-4 text-center">
            <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center mx-auto mb-4">
              <Upload className="w-7 h-7 text-white" />
            </div>
            <h1 className="text-2xl font-bold">Upload Your Bid</h1>
            <p className="text-blue-100 mt-2">Quick and easy bid submission</p>
          </div>
        </div>

        <div className="max-w-2xl mx-auto px-4 py-8">
          {/* Project Info Card */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Building2 className="w-5 h-5 text-blue-600" />
                Project Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <p className="text-sm text-slate-500">Project Name</p>
                <p className="font-semibold">{quickInviteData?.project?.name}</p>
              </div>
              {quickInviteData?.project?.address && (
                <div>
                  <p className="text-sm text-slate-500">Address</p>
                  <p>{quickInviteData?.project?.address}</p>
                </div>
              )}
              {quickInviteData?.invite?.trade_type && (
                <div>
                  <p className="text-sm text-slate-500">Trade</p>
                  <Badge variant="outline">{quickInviteData?.invite?.trade_type}</Badge>
                </div>
              )}
              <div>
                <p className="text-sm text-slate-500">Requested By</p>
                <p>{quickInviteData?.owner?.business_name || quickInviteData?.owner?.name || 'Project Owner'}</p>
              </div>
              {quickInviteData?.invite?.message && (
                <div className="p-4 bg-blue-50 border border-blue-100 rounded-lg mt-4">
                  <p className="text-sm text-blue-600 font-medium mb-1">Message from owner:</p>
                  <p className="text-slate-700 italic">&ldquo;{quickInviteData?.invite?.message}&rdquo;</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Upload Card */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Upload Your Bid Document</CardTitle>
              <CardDescription>
                Upload your existing bid document. We&apos;ll automatically extract the details for review.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div 
                className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                  quickUploadFile ? 'border-green-400 bg-green-50' : 'border-slate-300 hover:border-blue-400'
                }`}
              >
                <input
                  type="file"
                  id="quick-bid-file"
                  data-testid="quick-upload-file-input"
                  className="hidden"
                  accept=".pdf,.xlsx,.xls,.csv,.jpg,.jpeg,.png,.webp"
                  onChange={(e) => setQuickUploadFile(e.target.files?.[0] || null)}
                />
                <label htmlFor="quick-bid-file" className="cursor-pointer">
                  {quickUploadFile ? (
                    <div className="flex items-center justify-center gap-3">
                      <CheckCircle2 className="w-8 h-8 text-green-600" />
                      <div className="text-left">
                        <p className="font-medium text-green-700">{quickUploadFile.name}</p>
                        <p className="text-sm text-green-600">{(quickUploadFile.size / 1024).toFixed(1)} KB</p>
                      </div>
                    </div>
                  ) : (
                    <>
                      <Upload className="w-10 h-10 text-slate-400 mx-auto mb-3" />
                      <p className="font-medium text-slate-700">Click to upload your bid document</p>
                      <p className="text-sm text-slate-500 mt-1">PDF, Excel, CSV, or Image files supported</p>
                    </>
                  )}
                </label>
              </div>

              <div>
                <Label htmlFor="quick-notes">Additional Notes (Optional)</Label>
                <Textarea
                  id="quick-notes"
                  data-testid="quick-upload-notes"
                  placeholder="Add any notes or clarifications about your bid..."
                  value={quickUploadNotes}
                  onChange={(e) => setQuickUploadNotes(e.target.value)}
                  rows={3}
                  className="mt-2"
                />
              </div>

              <Alert className="bg-blue-50 border-blue-200">
                <Sparkles className="w-4 h-4 text-blue-600" />
                <AlertDescription className="text-sm">
                  Our AI will automatically extract line items and pricing from your document. 
                  You can review and edit the details after submission.
                </AlertDescription>
              </Alert>

              <Button 
                onClick={handleQuickBidUpload}
                disabled={!quickUploadFile || isUploadingQuickBid}
                data-testid="quick-upload-submit-btn"
                className="w-full bg-blue-600 hover:bg-blue-700"
                size="lg"
              >
                {isUploadingQuickBid ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Uploading & Processing...</>
                ) : (
                  <><Upload className="w-4 h-4 mr-2" /> Submit Bid</>
                )}
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Revision Mode UI
  if (revisionMode) {
    if (isLoading) {
      return (
        <div className="min-h-screen bg-slate-100 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-amber-600" />
        </div>
      );
    }

    // Show error state for revision failures
    if (revisionError) {
      return (
        <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
          <Card className="max-w-md w-full">
            <CardContent className="pt-8 pb-6 text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertCircle className="w-8 h-8 text-red-600" />
              </div>
              <h2 className="text-2xl font-bold text-slate-800 mb-2">Unable to Load Revision</h2>
              <p className="text-slate-600 mb-4">{revisionError}</p>
              <p className="text-sm text-slate-500 mb-6">
                This revision link may have expired or already been completed. Please contact the project owner for assistance.
              </p>
              <Button 
                onClick={() => window.location.reload()} 
                variant="outline"
                data-testid="retry-revision-btn"
              >
                Try Again
              </Button>
            </CardContent>
          </Card>
        </div>
      );
    }

    if (revisionSuccess) {
      return (
        <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
          <Card className="max-w-lg w-full">
            <CardContent className="pt-8 pb-6 text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-slate-800 mb-2">Bid Revised Successfully!</h2>
              <p className="text-slate-600 mb-6">
                Your revised bid has been submitted. {revisionData?.owner?.business_name || revisionData?.owner?.name || 'The project owner'} will review your changes.
              </p>
              <div className="p-4 bg-slate-50 rounded-lg text-left">
                <p className="text-sm text-slate-500 mb-1">Project</p>
                <p className="font-medium">{revisionData?.project?.name}</p>
                <p className="text-sm text-slate-500 mt-3 mb-1">New Total</p>
                <p className="text-xl font-bold text-green-600">${parseFloat(revisionTotal).toLocaleString()}</p>
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }

    const revisionTypeDisplay = {
      'general': 'General Revision',
      'price_adjustment': 'Price Adjustment',
      'add_options': 'Additional Options Needed',
      'clarification': 'Clarification Needed'
    }[revisionData?.revision?.revision_type] || 'Revision';

    return (
      <div className="min-h-screen bg-slate-100">
        {/* Header */}
        <div className="bg-gradient-to-r from-amber-600 to-amber-700 text-white py-8">
          <div className="max-w-3xl mx-auto px-4 text-center">
            <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center mx-auto mb-4">
              <Edit2 className="w-7 h-7 text-white" />
            </div>
            <h1 className="text-2xl font-bold">Revise Your Bid</h1>
            <p className="text-amber-100 mt-2">A revision has been requested</p>
          </div>
        </div>

        <div className="max-w-3xl mx-auto px-4 py-8">
          {/* Revision Request Info */}
          <Card className="mb-6 border-amber-200">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-amber-600" />
                  Revision Request
                </CardTitle>
                <Badge className="bg-amber-100 text-amber-700">{revisionTypeDisplay}</Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-slate-500">Project</p>
                  <p className="font-semibold">{revisionData?.project?.name}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Bid Request</p>
                  <p className="font-medium">{revisionData?.bid_request?.title}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Requested By</p>
                  <p>{revisionData?.owner?.business_name || revisionData?.owner?.name || 'Project Owner'}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Current Bid Amount</p>
                  <p className="font-bold text-lg">${revisionData?.bid_response?.total_amount?.toLocaleString()}</p>
                </div>
              </div>
              
              {/* Owner's Message */}
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-sm text-amber-700 font-medium mb-2">Message from owner:</p>
                <p className="text-slate-800 whitespace-pre-wrap">{revisionData?.revision?.message}</p>
              </div>
            </CardContent>
          </Card>

          {/* Revision Form */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Update Your Bid</CardTitle>
              <CardDescription>
                Modify your line items and pricing based on the requested changes.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Line Items */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="font-semibold">Line Items</Label>
                  <Button type="button" variant="outline" size="sm" onClick={addRevisionLineItem}>
                    <Plus className="w-4 h-4 mr-1" /> Add Item
                  </Button>
                </div>
                
                {revisionLineItems.length === 0 ? (
                  <div className="text-center py-6 border border-dashed rounded-lg">
                    <p className="text-slate-500">No line items yet</p>
                    <Button variant="link" onClick={addRevisionLineItem}>Add your first line item</Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {revisionLineItems.map((item, index) => (
                      <div key={item.id || index} className="p-3 border rounded-lg bg-slate-50">
                        <div className="grid grid-cols-12 gap-2">
                          <div className="col-span-5">
                            <Input
                              placeholder="Description"
                              value={item.description}
                              onChange={(e) => updateRevisionLineItem(index, 'description', e.target.value)}
                            />
                          </div>
                          <div className="col-span-2">
                            <Input
                              type="number"
                              placeholder="Qty"
                              value={item.quantity}
                              onChange={(e) => updateRevisionLineItem(index, 'quantity', e.target.value)}
                            />
                          </div>
                          <div className="col-span-2">
                            <Input
                              type="number"
                              placeholder="Unit Price"
                              value={item.unit_price}
                              onChange={(e) => updateRevisionLineItem(index, 'unit_price', e.target.value)}
                            />
                          </div>
                          <div className="col-span-2">
                            <Input
                              type="number"
                              placeholder="Total"
                              value={item.total}
                              onChange={(e) => updateRevisionLineItem(index, 'total', e.target.value)}
                              className="bg-slate-100"
                            />
                          </div>
                          <div className="col-span-1 flex items-center justify-center">
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              className="h-8 w-8 text-red-500 hover:text-red-700"
                              onClick={() => removeRevisionLineItem(index)}
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Grand Total */}
              <div className="p-4 bg-slate-100 rounded-lg">
                <div className="flex justify-between items-center">
                  <Label className="font-semibold text-lg">Grand Total</Label>
                  <div className="flex items-center gap-2">
                    <span className="text-slate-500">$</span>
                    <Input
                      type="number"
                      value={revisionTotal}
                      onChange={(e) => setRevisionTotal(e.target.value)}
                      className="w-40 text-right text-xl font-bold"
                    />
                  </div>
                </div>
              </div>

              {/* Notes */}
              <div>
                <Label>Notes / Comments</Label>
                <Textarea
                  className="mt-1.5"
                  placeholder="Add any notes about the changes you've made..."
                  value={revisionNotes}
                  onChange={(e) => setRevisionNotes(e.target.value)}
                  rows={3}
                />
              </div>

              {/* Submit Button */}
              <Button 
                onClick={handleSubmitRevision}
                disabled={isSubmittingRevision}
                className="w-full bg-amber-600 hover:bg-amber-700"
                size="lg"
                data-testid="submit-revision-btn"
              >
                {isSubmittingRevision ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting Revision...</>
                ) : (
                  <><CheckCircle2 className="w-4 h-4 mr-2" /> Submit Revised Bid</>
                )}
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (!contractorEmail) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6">
            <div className="text-center">
              <AlertCircle className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
              <h2 className="text-xl font-bold mb-2">Access Required</h2>
              <p className="text-slate-600">
                Please use the link provided in your email to access your bid requests.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show error state if there was a problem loading
  if (error && !isLoading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6">
            <div className="text-center">
              <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
              <h2 className="text-xl font-bold mb-2">Something Went Wrong</h2>
              <p className="text-slate-600 mb-4">{error}</p>
              <Button onClick={() => window.location.reload()}>
                Try Again
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Header */}
      <div className="bg-gradient-to-r from-slate-800 to-slate-700 text-white py-6">
        <div className="max-w-5xl mx-auto px-4">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-orange-500 rounded-lg flex items-center justify-center">
              <Gavel className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold">DrawReady</h1>
              <Badge className="bg-orange-500/20 text-orange-200 border-orange-400/30">Contractor Portal</Badge>
            </div>
          </div>
          <p className="text-slate-300 text-sm mt-2">Welcome back! View and respond to bid requests.</p>
        </div>
      </div>

      {/* Privacy Notice */}
      <div className="max-w-5xl mx-auto px-4 py-4">
        <Alert className="bg-green-50 border-green-200">
          <Lock className="w-4 h-4 text-green-600" />
          <AlertDescription className="text-green-700">
            You only see bid requests sent specifically to you. Other contractors cannot see your bids.
          </AlertDescription>
        </Alert>

        {/* v2.5.2: Set Password CTA — only shown if the contractor has not
            created a password yet. Lets them log in via the contractor
            login page from any device instead of using email-link access. */}
        {accountInfo && !accountInfo.has_password && (
          <div className="mt-3 bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-start gap-3 flex-1 min-w-[260px]">
              <Lock className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-blue-900">
                  Set up a password for faster login
                </p>
                <p className="text-xs text-blue-700 mt-0.5">
                  Right now you access this portal via your email link. Create a password so you can log in directly at the contractor login page from any device.
                </p>
              </div>
            </div>
            <Button
              size="sm"
              onClick={() => setShowSetPasswordModal(true)}
              data-testid="set-password-cta"
              className="bg-blue-600 hover:bg-blue-700"
            >
              Set Password
            </Button>
          </div>
        )}
      </div>

      {/* Main Content */}
      <div className="max-w-5xl mx-auto px-4 pb-8">
        <Tabs value={activeTab} onValueChange={(val) => {
          setActiveTab(val);
          if (val === 'messages') {
            loadConversations();
          }
        }}>
          <TabsList className="mb-6">
            <TabsTrigger value="bids" data-testid="tab-bids">
              <Gavel className="w-4 h-4 mr-2" />
              Bid Requests ({bidRequests.filter(b => b.status === 'open').length})
            </TabsTrigger>
            <TabsTrigger value="messages" data-testid="tab-messages">
              <Mail className="w-4 h-4 mr-2" />
              Messages
              {conversations.reduce((sum, c) => sum + (c.unread_count || 0), 0) > 0 && (
                <span className="ml-2 w-5 h-5 bg-blue-500 text-white text-xs rounded-full flex items-center justify-center">
                  {conversations.reduce((sum, c) => sum + (c.unread_count || 0), 0)}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="schedule" data-testid="tab-schedule">
              <Calendar className="w-4 h-4 mr-2" />
              Schedule ({scheduleItems.filter(s => s.approval_status === 'pending').length})
            </TabsTrigger>
            <TabsTrigger value="forms" data-testid="tab-forms">
              <FileText className="w-4 h-4 mr-2" />
              Forms
            </TabsTrigger>
          </TabsList>

          {/* Messages Tab */}
          <TabsContent value="messages">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Mail className="w-5 h-5" />
                  Messages
                </CardTitle>
                <CardDescription>Chat with your clients. Messages auto-delete 24hrs after read.</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="border rounded-lg overflow-hidden" style={{ height: '500px' }}>
                  <div className="flex h-full">
                    {/* Conversation List */}
                    <div className="w-64 border-r bg-slate-50 flex flex-col">
                      <div className="flex-1 overflow-y-auto">
                        {conversations.length === 0 ? (
                          <div className="p-6 text-center text-slate-500">
                            <Mail className="w-10 h-10 mx-auto mb-2 opacity-30" />
                            <p className="text-sm">No conversations yet</p>
                          </div>
                        ) : (
                          conversations.map((conv) => (
                            <div
                              key={conv.client_id}
                              className={`p-3 border-b cursor-pointer transition-colors ${
                                selectedClientConversation?.client_id === conv.client_id
                                  ? 'bg-blue-50 border-l-4 border-l-blue-500'
                                  : 'hover:bg-slate-100'
                              }`}
                              onClick={() => handleSelectClientConversation(conv)}
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex-1 min-w-0">
                                  <div className="font-medium text-sm truncate">{conv.client_name}</div>
                                  <p className="text-xs text-slate-500 mt-1 truncate">{conv.last_message}</p>
                                </div>
                                {conv.unread_count > 0 && (
                                  <span className="w-5 h-5 bg-blue-500 text-white text-xs rounded-full flex items-center justify-center ml-2">
                                    {conv.unread_count}
                                  </span>
                                )}
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    </div>

                    {/* Chat Area */}
                    <div className="flex-1 flex flex-col">
                      {selectedClientConversation ? (
                        <>
                          <div className="p-3 border-b bg-white">
                            <div className="font-semibold">{selectedClientConversation.client_name}</div>
                          </div>
                          <div className="flex-1 overflow-y-auto p-4 bg-slate-50">
                            {selectedClientConversation.messages?.length === 0 ? (
                              <div className="h-full flex items-center justify-center text-slate-500">
                                <p className="text-sm">No messages yet</p>
                              </div>
                            ) : (
                              <div className="space-y-3">
                                {selectedClientConversation.messages?.map((msg) => (
                                  <div
                                    key={msg.id}
                                    className={`flex ${msg.sender_type === 'contractor' ? 'justify-end' : 'justify-start'}`}
                                  >
                                    <div
                                      className={`max-w-[70%] rounded-2xl px-4 py-2 ${
                                        msg.sender_type === 'contractor'
                                          ? 'bg-blue-500 text-white rounded-br-sm'
                                          : 'bg-white border shadow-sm rounded-bl-sm'
                                      }`}
                                    >
                                      <p className="text-sm">{msg.message}</p>
                                      <div className={`flex items-center gap-1 mt-1 text-xs ${
                                        msg.sender_type === 'contractor' ? 'text-blue-100' : 'text-slate-400'
                                      }`}>
                                        <span>{new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                                        {msg.sender_type === 'contractor' && (
                                          <span className="ml-1">{msg.recipient_read ? '✓✓' : '✓'}</span>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                ))}
                                <div ref={messagesEndRef} />
                              </div>
                            )}
                          </div>
                          <div className="p-3 border-t bg-white">
                            <div className="flex items-end gap-2">
                              <Textarea
                                placeholder="Type a message..."
                                value={bidMessageText}
                                onChange={(e) => setBidMessageText(e.target.value)}
                                className="flex-1 resize-none"
                                rows={1}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' && !e.shiftKey) {
                                    e.preventDefault();
                                    handleSendToClient();
                                  }
                                }}
                              />
                              <Button
                                onClick={handleSendToClient}
                                disabled={isSendingBidMessage || !bidMessageText.trim()}
                                size="sm"
                              >
                                {isSendingBidMessage ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                              </Button>
                            </div>
                          </div>
                        </>
                      ) : (
                        <div className="flex-1 flex items-center justify-center text-slate-500">
                          <div className="text-center">
                            <Mail className="w-12 h-12 mx-auto mb-2 opacity-30" />
                            <p className="text-sm">Select a conversation</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Bids Tab */}
          <TabsContent value="bids">
            {isLoading ? (
              <div className="text-center py-12">
                <div className="animate-spin w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full mx-auto"></div>
              </div>
            ) : selectedBid ? (
              // Bid Response Form
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <Badge className="mb-2">BID REQUEST</Badge>
                      <CardTitle>{selectedBid.title}</CardTitle>
                      <CardDescription>
                        {selectedBid.project?.name} • {selectedBid.project?.address}
                      </CardDescription>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-slate-500">Due Date</p>
                      <p className="text-lg font-bold text-red-600">
                        {formatDate(selectedBid.due_date)}
                      </p>
                      <p className="text-sm text-slate-500">
                        {getDaysRemaining(selectedBid.due_date)} days remaining
                      </p>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Scope */}
                  <div>
                    <h4 className="font-medium mb-2">Scope of Work</h4>
                    <div className="bg-slate-50 rounded-lg p-4">
                      <p className="whitespace-pre-wrap text-slate-700">{selectedBid.scope_of_work}</p>
                    </div>
                  </div>

                  {/* Attachments */}
                  {(selectedBid.documents?.length > 0 || selectedBid.scope_attachments?.length > 0) && (
                    <div>
                      <h4 className="font-medium mb-2">Attachments</h4>
                      <div className="flex flex-wrap gap-2">
                        {selectedBid.documents?.map(doc => (
                          <Button
                            key={doc.id}
                            variant="outline"
                            size="sm"
                            data-testid={`bid-doc-download-${doc.id}`}
                            onClick={() => handleDownloadDocument(doc.id, doc.file_name)}
                          >
                            <FileText className="w-4 h-4 mr-2" />
                            {doc.file_name}
                            <Download className="w-4 h-4 ml-2" />
                          </Button>
                        ))}
                        {selectedBid.scope_attachments?.map(att => (
                          <Button
                            key={att.id}
                            variant="outline"
                            size="sm"
                            data-testid={`scope-attachment-download-${att.id}`}
                            onClick={() => handleDownloadScopeAttachment(selectedBid.id, att.id, att.filename)}
                          >
                            <FileText className="w-4 h-4 mr-2" />
                            {att.filename}
                            <Download className="w-4 h-4 ml-2" />
                          </Button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Questions Section */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-medium text-blue-900 flex items-center gap-2">
                          <MessageSquare className="w-4 h-4" />
                          Have Questions?
                        </h4>
                        <p className="text-sm text-blue-700 mt-1">
                          Need clarification about the scope? Ask the client directly here.
                        </p>
                      </div>
                      <Button 
                        variant="outline" 
                        className="bg-white border-blue-300 text-blue-700 hover:bg-blue-100"
                        onClick={() => {
                          loadBidMessages(selectedBid.id);
                          setShowBidMessageDialog(true);
                        }}
                      >
                        <MessageSquare className="w-4 h-4 mr-2" />
                        Ask a Question
                      </Button>
                    </div>
                    {bidMessages[selectedBid.id]?.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-blue-200">
                        <p className="text-xs text-blue-600 mb-2">{bidMessages[selectedBid.id].length} message(s) in this thread</p>
                        <div className="space-y-2 max-h-[150px] overflow-y-auto">
                          {bidMessages[selectedBid.id].slice(-3).map((msg) => (
                            <div key={msg.id} className={`text-sm p-2 rounded ${msg.sender_type === 'contractor' ? 'bg-blue-100' : 'bg-white'}`}>
                              <span className="font-medium">{msg.sender_name}:</span> {msg.message.substring(0, 100)}{msg.message.length > 100 ? '...' : ''}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Line Items Form */}
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-medium flex items-center gap-2">
                        <DollarSign className="w-4 h-4" />
                        Submit Your Bid (Itemized)
                      </h4>
                      <div className="flex gap-2">
                        {/* AI Extract Button */}
                        <div className="relative">
                          <input
                            type="file"
                            id="bid-extract-upload"
                            className="hidden"
                            accept=".pdf,.jpg,.jpeg,.png,.webp,.xlsx,.xls,.csv,.docx,.doc"
                            onChange={handleFileUpload}
                            disabled={isExtracting}
                          />
                          <Button 
                            type="button" 
                            variant="outline" 
                            size="sm"
                            disabled={isExtracting}
                            onClick={() => document.getElementById('bid-extract-upload').click()}
                            className="border-purple-300 text-purple-700 hover:bg-purple-50"
                            data-testid="ai-extract-btn"
                          >
                            {isExtracting ? (
                              <>
                                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                                Extracting...
                              </>
                            ) : (
                              <>
                                <Sparkles className="w-4 h-4 mr-1" />
                                Upload & Extract
                              </>
                            )}
                          </Button>
                        </div>
                        <Button type="button" variant="outline" size="sm" onClick={addLineItem}>
                          <Plus className="w-4 h-4 mr-1" /> Add Line Item
                        </Button>
                      </div>
                    </div>
                    
                    {/* Transparency Notice */}
                    <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <p className="text-sm text-blue-800">
                        <strong>📊 Transparency Required:</strong> Please specify how your bid is calculated (per sqft, hourly, per unit, etc.) for each line item. This helps ensure fair comparison between all bids.
                      </p>
                    </div>
                    
                    {/* Upload info */}
                    {uploadedFile && (
                      <div className="mb-3 flex items-center gap-2 text-sm text-purple-600 bg-purple-50 p-2 rounded-lg">
                        <FileText className="w-4 h-4" />
                        <span>Extracted from: {uploadedFile.name}</span>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="h-6 px-2 text-purple-600"
                          onClick={() => setUploadedFile(null)}
                        >
                          <X className="w-3 h-3" />
                        </Button>
                      </div>
                    )}
                    
                    <div className="border rounded-lg overflow-hidden">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-slate-50 border-b">
                            <th className="text-left py-2 px-3">Description</th>
                            <th className="text-left py-2 px-3 w-20">Qty</th>
                            <th className="text-left py-2 px-3 w-28">Pricing Type</th>
                            <th className="text-right py-2 px-3 w-28">Rate</th>
                            <th className="text-right py-2 px-3 w-28">Total</th>
                            <th className="w-10"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {lineItems.map((item, idx) => (
                            <tr key={idx} className="border-b">
                              <td className="py-2 px-2">
                                <Input 
                                  value={item.description}
                                  onChange={(e) => updateLineItem(idx, 'description', e.target.value)}
                                  placeholder="e.g., Labor, Materials, Installation"
                                  className="border-0 bg-transparent"
                                />
                              </td>
                              <td className="py-2 px-2">
                                <Input 
                                  type="number"
                                  value={item.quantity}
                                  onChange={(e) => updateLineItem(idx, 'quantity', e.target.value)}
                                  placeholder="Qty"
                                  className="border-0 bg-transparent text-center"
                                />
                              </td>
                              <td className="py-2 px-1">
                                <Select 
                                  value={item.unit || ''} 
                                  onValueChange={(v) => updateLineItem(idx, 'unit', v)}
                                >
                                  <SelectTrigger className="h-8 text-xs border-0 bg-transparent">
                                    <SelectValue placeholder="Select..." />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="sqft">per sqft</SelectItem>
                                    <SelectItem value="lf">per linear ft</SelectItem>
                                    <SelectItem value="ea">each/unit</SelectItem>
                                    <SelectItem value="hr">per hour</SelectItem>
                                    <SelectItem value="day">per day</SelectItem>
                                    <SelectItem value="lot">lump sum</SelectItem>
                                    <SelectItem value="sf">per sq yard</SelectItem>
                                    <SelectItem value="cf">per cubic ft</SelectItem>
                                    <SelectItem value="ton">per ton</SelectItem>
                                  </SelectContent>
                                </Select>
                              </td>
                              <td className="py-2 px-2">
                                <div className="relative">
                                  <span className="absolute left-1 top-1/2 -translate-y-1/2 text-slate-400 text-xs">$</span>
                                  <Input 
                                    type="number"
                                    step="0.01"
                                    value={item.unit_price}
                                    onChange={(e) => updateLineItem(idx, 'unit_price', e.target.value)}
                                    placeholder="0.00"
                                    className="border-0 bg-transparent text-right pl-4"
                                  />
                                </div>
                              </td>
                              <td className="py-2 px-2">
                                <div className="relative">
                                  <span className="absolute left-1 top-1/2 -translate-y-1/2 text-slate-400 text-xs">$</span>
                                  <Input 
                                    type="number"
                                    step="0.01"
                                    value={item.total}
                                    onChange={(e) => updateLineItem(idx, 'total', e.target.value)}
                                    placeholder="0.00"
                                    className="border-0 bg-transparent text-right font-medium pl-4"
                                  />
                                </div>
                              </td>
                              <td className="py-2 px-2">
                                {lineItems.length > 1 && (
                                  <Button 
                                    type="button" 
                                    variant="ghost" 
                                    size="icon"
                                    onClick={() => removeLineItem(idx)}
                                  >
                                    <X className="w-4 h-4" />
                                  </Button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr className="bg-slate-100 font-semibold">
                            <td colSpan={4} className="py-3 px-3 text-right">Total Bid Amount:</td>
                            <td className="py-3 px-3 text-right text-lg text-green-600">
                              ${calculateTotal().toLocaleString()}
                            </td>
                            <td></td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                    
                    {/* Pricing breakdown hint */}
                    {lineItems.length > 0 && lineItems.some(item => !item.unit) && (
                      <p className="mt-2 text-xs text-amber-600 flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />
                        Please select a pricing type for each line item for transparency
                      </p>
                    )}
                  </div>

                  {/* Additional Info */}
                  {stageBreakdown.length > 0 ? (
                    /* v2.5.5.5/v2.5.5.7: Per-stage timing for multi-stage trades
                       (Plumbing → Ground/Rough/Trim, Electrical → Rough/Trim/Final, etc.)
                       — supports custom stages and per-building input. */
                    (() => {
                      const buildingCount = Math.max(1, parseInt(selectedBid?.project?.buildings || 1, 10));
                      const updateUniformCrew = (idx, v) => setStageBreakdown(prev => prev.map((s, i) => i === idx ? {
                        ...s,
                        crew_size: v === '' ? '' : (parseInt(v, 10) >= 0 ? parseInt(v, 10) : ''),
                      } : s));
                      const updateUniformDays = (idx, v) => setStageBreakdown(prev => prev.map((s, i) => i === idx ? {
                        ...s,
                        days: v === '' ? '' : (parseInt(v, 10) >= 0 ? parseInt(v, 10) : ''),
                      } : s));
                      const updatePerBuildingDays = (bIdx, stageName, v) => setPerBuildingStages(prev => ({
                        ...prev,
                        [bIdx]: {
                          ...(prev[bIdx] || {}),
                          [stageName]: v === '' ? '' : (parseInt(v, 10) >= 0 ? parseInt(v, 10) : ''),
                        },
                      }));
                      const removeStage = (idx) => setStageBreakdown(prev => prev.filter((_, i) => i !== idx));
                      const addCustomStage = () => {
                        const name = newStageName.trim();
                        if (!name) return;
                        if (stageBreakdown.some(s => s.stage_name.toLowerCase() === name.toLowerCase())) {
                          toast.error(`"${name}" is already in your stage list.`);
                          return;
                        }
                        setStageBreakdown(prev => [...prev, { stage_name: name, days: '', is_custom: true }]);
                        setNewStageName('');
                      };
                      const uniformTotal = stageBreakdown.reduce((s, st) => s + (parseInt(st.days, 10) || 0), 0);
                      const perBuildingTotal = Object.values(perBuildingStages).reduce(
                        (s, b) => s + Object.values(b).reduce((ss, v) => ss + (parseInt(v, 10) || 0), 0), 0,
                      );
                      return (
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-testid="bid-stage-breakdown">
                          <div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
                            <div className="flex items-center gap-2">
                              <Label className="text-sm font-semibold text-blue-900">
                                Days to complete each stage
                              </Label>
                              <span className="text-[10px] uppercase tracking-wider text-blue-600 font-bold px-2 py-0.5 bg-white border border-blue-200 rounded">
                                {selectedBid?.trade_type || 'Multi-stage'}
                              </span>
                            </div>
                            {buildingCount > 1 && (
                              <label className="inline-flex items-center gap-1.5 text-xs text-slate-700 cursor-pointer bg-white border border-blue-200 rounded-full px-2.5 py-1">
                                <input
                                  type="checkbox"
                                  checked={sameForAllBuildings}
                                  onChange={(e) => setSameForAllBuildings(e.target.checked)}
                                  className="rounded"
                                  data-testid="bid-same-for-all-buildings"
                                />
                                Same days for all {buildingCount} buildings
                              </label>
                            )}
                          </div>
                          <p className="text-xs text-blue-700 mb-3">
                            Tell us how many days each stage will take. These numbers are used to slot you into the project schedule after award.
                          </p>

                          {sameForAllBuildings ? (
                            <div className="space-y-2">
                              {stageBreakdown.map((stage, idx) => {
                                const defaultKey = `${(selectedBid?.trade_type || '').toLowerCase()}.${stage.stage_name.toLowerCase()}`;
                                const suggested = stageDefaults[defaultKey];
                                const needsCrew = stageRequiresCrew.has(defaultKey);
                                return (
                                  <div key={`${stage.stage_name}-${idx}`} className="flex items-center gap-2 flex-wrap">
                                    <span className="text-xs font-semibold text-slate-700 w-40 flex-shrink-0 flex items-center gap-1">
                                      {stage.stage_name}
                                      {stage.is_custom && (
                                        <span className="text-[9px] uppercase font-bold text-amber-700 bg-amber-100 border border-amber-200 px-1 rounded">custom</span>
                                      )}
                                    </span>
                                    <Input
                                      type="number" min="0" step="1"
                                      value={stage.days}
                                      onChange={(e) => updateUniformDays(idx, e.target.value)}
                                      placeholder={suggested ? `~${suggested}` : 'e.g. 3'}
                                      className="w-20 text-center"
                                      data-testid={`stage-days-${idx}`}
                                    />
                                    <span className="text-xs text-slate-600">days</span>
                                    {needsCrew && (
                                      <>
                                        <span className="text-xs text-slate-400 mx-1">·</span>
                                        <Input
                                          type="number" min="1" step="1"
                                          value={stage.crew_size ?? ''}
                                          onChange={(e) => updateUniformCrew(idx, e.target.value)}
                                          placeholder="2"
                                          className="w-16 text-center"
                                          data-testid={`stage-crew-${idx}`}
                                        />
                                        <span className="text-xs text-slate-600 whitespace-nowrap">guys / unit</span>
                                      </>
                                    )}
                                    {stage.is_custom && (
                                      <button
                                        type="button"
                                        onClick={() => removeStage(idx)}
                                        className="text-slate-400 hover:text-red-600 ml-1"
                                        data-testid={`remove-stage-${idx}`}
                                        title="Remove this custom stage"
                                      >
                                        <X className="w-3.5 h-3.5" />
                                      </button>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          ) : (
                            <div className="space-y-3">
                              {/* Crew-size inputs apply to all buildings — render
                                  them once above the per-building grid so
                                  drywall hangers/finishers don't get repeated. */}
                              {stageBreakdown.some(st => stageRequiresCrew.has(`${(selectedBid?.trade_type || '').toLowerCase()}.${st.stage_name.toLowerCase()}`)) && (
                                <div className="bg-white border border-blue-200 rounded-lg p-3" data-testid="bid-crew-sizes-block">
                                  <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-2">
                                    Crew size (applies to all buildings)
                                  </div>
                                  <div className="space-y-1.5">
                                    {stageBreakdown.map((st, idx) => {
                                      const dk = `${(selectedBid?.trade_type || '').toLowerCase()}.${st.stage_name.toLowerCase()}`;
                                      if (!stageRequiresCrew.has(dk)) return null;
                                      return (
                                        <div key={`crew-${st.stage_name}`} className="flex items-center gap-2">
                                          <span className="text-xs font-medium text-slate-700 w-36 flex-shrink-0">
                                            {st.stage_name}
                                          </span>
                                          <Input
                                            type="number" min="1" step="1"
                                            value={st.crew_size ?? ''}
                                            onChange={(e) => updateUniformCrew(idx, e.target.value)}
                                            placeholder="2"
                                            className="w-16 text-center"
                                            data-testid={`stage-crew-pb-${idx}`}
                                          />
                                          <span className="text-xs text-slate-600">guys / unit</span>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              )}
                              {[...Array(buildingCount)].map((_, bIdx) => (
                                <div key={bIdx} className="bg-white rounded-lg p-3 border border-blue-200" data-testid={`bid-building-block-${bIdx}`}>
                                  <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-2">
                                    Building {bIdx + 1}
                                  </div>
                                  <div className="space-y-1.5">
                                    {stageBreakdown.map((stage) => (
                                      <div key={stage.stage_name} className="flex items-center gap-2">
                                        <span className="text-xs font-medium text-slate-700 w-36 flex-shrink-0 flex items-center gap-1">
                                          {stage.stage_name}
                                          {stage.is_custom && (
                                            <span className="text-[9px] uppercase font-bold text-amber-700 bg-amber-100 border border-amber-200 px-1 rounded">custom</span>
                                          )}
                                        </span>
                                        <Input
                                          type="number" min="0" step="1"
                                          value={(perBuildingStages[bIdx] || {})[stage.stage_name] ?? ''}
                                          onChange={(e) => updatePerBuildingDays(bIdx, stage.stage_name, e.target.value)}
                                          placeholder="e.g. 3"
                                          className="w-20 text-center"
                                          data-testid={`stage-days-b${bIdx}-${stage.stage_name.replace(/\s+/g,'-')}`}
                                        />
                                        <span className="text-xs text-slate-600">days</span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}

                          {/* + Add custom stage */}
                          <div className="mt-3 flex items-center gap-2">
                            <Input
                              value={newStageName}
                              onChange={(e) => setNewStageName(e.target.value)}
                              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCustomStage(); } }}
                              placeholder="Add another stage (e.g. Sealing, Cure Time)"
                              className="flex-1 text-xs"
                              data-testid="new-stage-name-input"
                            />
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              onClick={addCustomStage}
                              disabled={!newStageName.trim()}
                              data-testid="add-custom-stage-btn"
                            >
                              <Plus className="w-3.5 h-3.5 mr-1" /> Add stage
                            </Button>
                          </div>

                          <div className="mt-3 pt-3 border-t border-blue-200 flex items-center justify-between">
                            <span className="text-xs font-semibold text-slate-700">
                              {sameForAllBuildings ? 'Total estimated days' : `Total across all ${buildingCount} buildings`}
                            </span>
                            <span className="text-base font-bold text-blue-900" data-testid="stage-breakdown-total">
                              {sameForAllBuildings ? uniformTotal : perBuildingTotal} days
                            </span>
                          </div>
                        </div>
                      );
                    })()
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label>Estimated Time to Complete</Label>
                        <div className="flex gap-2 mt-1">
                          <Input
                            type="number"
                            min="0"
                            step="1"
                            value={durationValue}
                            onChange={(e) => setDurationValue(e.target.value)}
                            placeholder="e.g., 2"
                            className="flex-1"
                            data-testid="bid-duration-value"
                          />
                          <select
                            value={durationUnit}
                            onChange={(e) => setDurationUnit(e.target.value)}
                            className="h-10 px-3 rounded-md border border-slate-200 bg-white text-sm font-medium text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            data-testid="bid-duration-unit"
                            aria-label="Duration unit"
                          >
                            <option value="days">Days</option>
                            <option value="weeks">Weeks</option>
                            <option value="months">Months</option>
                          </select>
                        </div>
                        {durationValue && (
                          <p className="text-xs text-slate-500 mt-1">
                            ≈ {durationToDays(durationValue, durationUnit)} days total
                          </p>
                        )}
                        <button
                          type="button"
                          onClick={() => {
                            // Convert the single duration into a stage breakdown
                            // with one custom stage so the contractor can add more.
                            const d = durationToDays(durationValue, durationUnit) || 0;
                            setStageBreakdown([{ stage_name: 'Work', days: d || '', is_custom: true }]);
                          }}
                          className="mt-2 text-[11px] text-blue-700 hover:text-blue-800 underline-offset-2 hover:underline"
                          data-testid="break-into-stages-btn"
                        >
                          Need to break this into multiple stages?
                        </button>
                      </div>
                    </div>
                  )}

                  <div>
                    <Label>Notes / Comments</Label>
                    <Textarea 
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder="Any additional notes about your bid..."
                      rows={3}
                    />
                  </div>

                  {/* Actions */}
                  <div className="flex justify-between pt-4 border-t">
                    <Button variant="outline" onClick={() => setSelectedBid(null)}>
                      <X className="w-4 h-4 mr-2" />
                      Cancel
                    </Button>
                    <Button 
                      onClick={handleSubmitBid}
                      disabled={isSubmitting || calculateTotal() === 0}
                      data-testid="submit-bid-btn"
                    >
                      {isSubmitting ? (
                        <>
                          <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                          Submitting...
                        </>
                      ) : (
                        <>
                          <Send className="w-4 h-4 mr-2" />
                          {selectedBid.has_responded ? 'Update Bid' : 'Submit Bid'}
                        </>
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ) : (
              // Bid List
              <div className="space-y-4">
                {bidRequests.length === 0 ? (
                  <Card>
                    <CardContent className="py-12 text-center">
                      <Gavel className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-slate-700">No Bid Requests</h3>
                      <p className="text-slate-500">You don&apos;t have any bid requests at this time.</p>
                    </CardContent>
                  </Card>
                ) : (
                  bidRequests.map(bid => (
                    <Card 
                      key={bid.id} 
                      className="hover:shadow-md transition-shadow cursor-pointer"
                      onClick={() => handleSelectBid(bid)}
                      data-testid={`bid-card-${bid.id}`}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <Badge className="bg-blue-100 text-blue-700">BID REQUEST</Badge>
                              {getStatusBadge(bid.status, bid.has_responded)}
                            </div>
                            {bid.project?.builder_company_name && (
                              <div className="flex items-center gap-2 mb-1.5" data-testid={`bid-builder-${bid.id}`}>
                                {bid.project?.builder_company_logo ? (
                                  <img src={bid.project.builder_company_logo} alt="" className="w-6 h-6 rounded object-contain bg-white border border-slate-200" />
                                ) : (
                                  <span className="w-6 h-6 rounded bg-slate-100 flex items-center justify-center">
                                    <Building2 className="w-3.5 h-3.5 text-slate-400" />
                                  </span>
                                )}
                                <span className="text-xs text-slate-500">Bidding for <span className="font-bold text-slate-800">{bid.project.builder_company_name}</span></span>
                              </div>
                            )}
                            <h3 className="font-semibold text-lg">{bid.title}</h3>
                            <p className="text-sm text-slate-500">
                              {bid.project?.name} • {bid.project?.address}
                            </p>
                            <p className="text-sm text-slate-600 mt-2 line-clamp-2">
                              {bid.scope_of_work}
                            </p>
                            
                            {bid.has_responded && bid.response && (
                              <div className="mt-3 bg-green-50 rounded-lg p-3">
                                <p className="text-sm text-green-700">
                                  <CheckCircle2 className="w-4 h-4 inline mr-1" />
                                  Your bid: <strong>${bid.response.total_amount?.toLocaleString()}</strong>
                                  {bid.response.estimated_days && (() => {
                                    const d = daysToDurationDisplay(bid.response.estimated_days);
                                    return ` • ${d.value} ${d.unit}`;
                                  })()}
                                </p>
                              </div>
                            )}
                          </div>
                          
                          <div className="text-right ml-4">
                            <p className="text-sm text-slate-500">Due Date</p>
                            <p className={`font-bold ${getDaysRemaining(bid.due_date) <= 3 ? 'text-red-600' : 'text-slate-900'}`}>
                              {formatDate(bid.due_date)}
                            </p>
                            <p className="text-sm text-slate-500">
                              {getDaysRemaining(bid.due_date)} days
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))
                )}
              </div>
            )}
          </TabsContent>

          {/* Schedule Tab */}
          <TabsContent value="schedule">
            {scheduleItems.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Calendar className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-slate-700">No Schedule Assignments</h3>
                  <p className="text-slate-500">You'll see schedules here when they're assigned to you.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {scheduleItems.map(item => {
                  const itemKey = item.id || item.phase_id || item.approval_id;
                  const isItemBased = item.type === 'items';
                  
                  return (
                    <Card key={itemKey} className="overflow-hidden">
                      <div 
                        className="p-4 cursor-pointer hover:bg-slate-50"
                        onClick={() => {
                          setExpandedSchedule(prev => ({ ...prev, [itemKey]: !prev[itemKey] }));
                          if (!messages[item.project_id]) {
                            loadMessages(item.project_id);
                          }
                        }}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          {expandedSchedule[itemKey] ? 
                            <ChevronDown className="w-5 h-5 text-slate-400" /> : 
                            <ChevronRight className="w-5 h-5 text-slate-400" />
                          }
                          <div>
                            <h3 className="font-semibold text-slate-900">
                              {isItemBased ? `${item.task_count || item.tasks?.length || 0} Schedule Items` : item.phase_name}
                            </h3>
                            <p className="text-sm text-slate-500">
                              {item.project_name} • {formatDate(item.start_date)} - {formatDate(item.end_date)}
                            </p>
                            {item.message && (
                              <p className="text-sm text-blue-600 mt-1 italic">"{item.message}"</p>
                            )}
                          </div>
                        </div>
                        <Badge className={
                          item.approval_status === 'approved' ? 'bg-green-100 text-green-700' :
                          item.approval_status === 'change_requested' ? 'bg-red-100 text-red-700' :
                          item.approval_status === 'pending' ? 'bg-amber-100 text-amber-700' :
                          'bg-slate-100 text-slate-600'
                        }>
                          {item.approval_status === 'approved' ? 'Approved' :
                           item.approval_status === 'change_requested' ? 'Change Requested' :
                           item.approval_status === 'pending' ? 'Pending Your Review' :
                           'Not Sent'}
                        </Badge>
                      </div>
                    </div>
                    
                    {expandedSchedule[itemKey] && (
                      <div className="border-t border-slate-100">
                        {/* Tasks */}
                        <div className="p-4">
                          <h4 className="text-sm font-medium text-slate-700 mb-2">
                            {isItemBased ? 'Schedule Items' : 'Tasks'}
                          </h4>
                          {item.tasks?.length > 0 ? (
                            <div className="space-y-2">
                              {item.tasks.map((task, idx) => (
                                <div key={task.id || idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                                  <div className="flex items-center gap-3">
                                    <span className="text-xs text-slate-400 w-5">{idx + 1}</span>
                                    <div>
                                      <span className="text-sm font-medium">{task.name}</span>
                                      {task.description && (
                                        <p className="text-xs text-slate-500">{task.description}</p>
                                      )}
                                    </div>
                                  </div>
                                  <span className="text-xs text-slate-500 bg-white px-2 py-1 rounded">
                                    {formatDate(task.start_date)} {task.end_date && task.end_date !== task.start_date && `→ ${formatDate(task.end_date)}`}
                                  </span>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-sm text-slate-500 italic">No tasks listed</p>
                          )}
                        </div>
                        
                        {/* Actions */}
                        {item.approval_status === 'pending' && (
                          <div className="px-4 py-3 bg-slate-50 border-t border-slate-100 flex gap-2">
                            <Button 
                              className="bg-green-600 hover:bg-green-700"
                              onClick={() => { 
                                setSelectedScheduleItem({...item, itemKey}); 
                                handleScheduleResponse('approved'); 
                              }}
                            >
                              <CheckCircle2 className="w-4 h-4 mr-2" />
                              Approve Schedule
                            </Button>
                            <Button 
                              variant="outline"
                              onClick={() => { 
                                setSelectedScheduleItem({...item, itemKey}); 
                                setShowApprovalDialog(true); 
                              }}
                            >
                              <Calendar className="w-4 h-4 mr-2" />
                              Request Date Change
                            </Button>
                            <Button 
                              variant="ghost"
                              onClick={() => { 
                                setSelectedScheduleItem({...item, itemKey}); 
                                setShowMessageDialog(true); 
                              }}
                            >
                              <MessageSquare className="w-4 h-4 mr-2" />
                              Message
                            </Button>
                          </div>
                        )}
                        
                        {/* Show change request details if exists */}
                        {item.approval_status === 'change_requested' && item.approval?.change_request_reason && (
                          <div className="px-4 py-3 bg-amber-50 border-t border-amber-100">
                            <p className="text-sm text-amber-800">
                              <strong>Your change request:</strong> {item.approval.change_request_reason}
                            </p>
                          </div>
                        )}
                        
                        {/* Messages */}
                        {messages[item.project_id]?.length > 0 && (
                          <div className="p-4 border-t border-slate-100">
                            <h4 className="text-sm font-medium text-slate-700 mb-2">Messages</h4>
                            <div className="space-y-2 max-h-48 overflow-y-auto">
                              {messages[item.project_id].map(msg => (
                                <div 
                                  key={msg.id} 
                                  className={`p-2 rounded text-sm ${
                                    msg.sender_type === 'contractor' ? 'bg-green-50 ml-8' : 'bg-blue-50 mr-8'
                                  }`}
                                >
                                  <p className="text-xs text-slate-500 mb-1">{msg.sender_name}</p>
                                  <p>{msg.message}</p>
                                </div>
                              ))}
                            </div>
                            <div className="mt-3 flex gap-2">
                              <Input 
                                placeholder="Reply..." 
                                value={messageText}
                                onChange={(e) => setMessageText(e.target.value)}
                                onKeyPress={(e) => e.key === 'Enter' && selectedScheduleItem?.project_id === item.project_id && handleSendMessage()}
                              />
                              <Button 
                                size="sm"
                                onClick={() => { setSelectedScheduleItem(item); handleSendMessage(); }}
                              >
                                <Send className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </Card>
                  )
                })}
              </div>
            )}
          </TabsContent>

          {/* Forms Tab */}
          <TabsContent value="forms">
            <Card>
              <CardContent className="py-12 text-center">
                <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-700">Forms & Documents</h3>
                <p className="text-slate-500">W-9, Certificates of Insurance, and other forms will appear here.</p>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Change Request Dialog */}
      <Dialog open={showApprovalDialog} onOpenChange={setShowApprovalDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Request Schedule Change</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Label>Reason for change request</Label>
            <Textarea 
              value={changeReason}
              onChange={(e) => setChangeReason(e.target.value)}
              placeholder="Please explain why you need different dates..."
              rows={4}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowApprovalDialog(false)}>Cancel</Button>
            <Button onClick={() => handleScheduleResponse('change_requested')}>
              Submit Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Message Dialog */}
      <Dialog open={showMessageDialog} onOpenChange={setShowMessageDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send Message</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Textarea 
              value={messageText}
              onChange={(e) => setMessageText(e.target.value)}
              placeholder="Type your message..."
              rows={4}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMessageDialog(false)}>Cancel</Button>
            <Button onClick={handleSendMessage}>
              <Send className="w-4 h-4 mr-2" />
              Send
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bid Question Message Dialog */}
      <Dialog open={showBidMessageDialog} onOpenChange={setShowBidMessageDialog}>
        <DialogContent className="sm:max-w-lg max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-blue-600" />
              Ask About This Bid
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto min-h-[150px] max-h-[250px] p-2">
            {!bidMessages[selectedBid?.id] || bidMessages[selectedBid?.id]?.length === 0 ? (
              <div className="text-center text-slate-500 py-4">
                <MessageSquare className="w-10 h-10 mx-auto mb-2 opacity-30" />
                <p className="text-sm">No messages yet. Ask your question below!</p>
              </div>
            ) : (
              <div className="space-y-3">
                {bidMessages[selectedBid?.id]?.map((msg) => (
                  <div 
                    key={msg.id} 
                    className={`p-3 rounded-lg max-w-[85%] ${
                      msg.sender_type === 'contractor' 
                        ? 'bg-blue-100 ml-auto' 
                        : 'bg-slate-100'
                    }`}
                  >
                    <div className="text-xs text-slate-500 mb-1">
                      {msg.sender_name} • {new Date(msg.created_at).toLocaleString()}
                    </div>
                    <p className="text-sm">{msg.message}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          <div className="border-t pt-4 space-y-2">
            <Textarea
              placeholder="Type your question about this bid..."
              value={bidMessageText}
              onChange={(e) => setBidMessageText(e.target.value)}
              rows={3}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSendBidMessage();
                }
              }}
            />
            <div className="flex justify-between items-center">
              <p className="text-xs text-slate-500">Press Enter to send</p>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setShowBidMessageDialog(false)}>Cancel</Button>
                <Button 
                  onClick={handleSendBidMessage}
                  disabled={isSendingBidMessage || !bidMessageText.trim()}
                >
                  {isSendingBidMessage ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                  Send
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* v2.5.2: Set Password modal */}
      <Dialog open={showSetPasswordModal} onOpenChange={setShowSetPasswordModal}>
        <DialogContent className="max-w-md" data-testid="set-password-modal">
          <DialogHeader>
            <DialogTitle>Create a Login Password</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-slate-600">
              Set a password so you can log in at the contractor portal from any device — no more hunting through your inbox for the right link.
            </p>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wide text-slate-600">
                Email
              </Label>
              <Input
                value={contractorEmail}
                disabled
                className="mt-1 bg-slate-50"
              />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wide text-slate-600">
                New password (min 8 characters)
              </Label>
              <Input
                type="password"
                value={newPassword}
                onChange={e => setNewPassword(e.target.value)}
                placeholder="••••••••"
                className="mt-1"
                data-testid="new-password-input"
                autoComplete="new-password"
              />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wide text-slate-600">
                Confirm password
              </Label>
              <Input
                type="password"
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                placeholder="••••••••"
                className="mt-1"
                data-testid="confirm-password-input"
                autoComplete="new-password"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowSetPasswordModal(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSetPassword}
              disabled={isSettingPassword || !newPassword || !confirmPassword}
              data-testid="confirm-set-password-btn"
              className="bg-blue-600 hover:bg-blue-700"
            >
              {isSettingPassword ? 'Saving…' : 'Set Password'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Wrap the component with ErrorBoundary
function ContractorBidPortal() {
  return (
    <ErrorBoundary>
      <ContractorBidPortalInner />
    </ErrorBoundary>
  );
}

export default ContractorBidPortal;
